/**
 * Modul som används under receptförskrivningsflödet, för att kunna hålla en/flera förskrivningar i olika steg i
 * skicka-flödet
 */

import { baseActions, baseState, BaseState } from "@/store/modules/helpers/baseState";
import { NLLIssue } from "@/tabs/Receptforskrivning/helpers";
import { ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
import cloneDeep from "lodash/cloneDeep";

export type StoredReceptforskrivning = {
    id: string;
    nplId: string;
    lakemedelId: string;
    forhandsvisning: string;
    validationMessages: Array<NLLIssue>;
    errorMessages?: Array<NLLIssue>;
    visa_administrering?: string[];
    sparr_legal_ground?: string;
    sparr_subject_type?: string;
    apotek?: ReceptforskrivningDisplay["apotek"];
    [key: string]: unknown;
};

export type SendStatus = "Default" | "Success" | "Error" | "Loading";

type State = BaseState & {
    receptforskrivningar: Map<
        string,
        StoredReceptforskrivning & {
            [key: string]: unknown;
        }
    >;
    status: Map<string, SendStatus>;
};

export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
};

const getDefaultState: () => State = () => {
    return {
        receptforskrivningar: new Map(),
        status: new Map(),
        ...cloneDeep(baseState),
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        receptforskrivningar: (state: State) => [...state.receptforskrivningar.values()],
        receptforskrivningById:
            (state: State) =>
            ({ id }: { id?: StoredReceptforskrivning["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.receptforskrivningar.get(id);
            },
        statusById:
            (state: State) =>
            ({ id }: { id?: StoredReceptforskrivning["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.status.get(id) ?? "Default";
            },
    },
    mutations: {
        reset: (state: State) => {
            Object.assign(state, getDefaultState());
        },
        addReceptforskrivning: (
            state: State,
            {
                receptforskrivning,
            }: {
                receptforskrivning: StoredReceptforskrivning;
            }
        ) => {
            const receptforskrivningar = new Map(state.receptforskrivningar);

            receptforskrivningar.set(receptforskrivning.id, receptforskrivning);

            state.receptforskrivningar = receptforskrivningar;
        },
        updateReceptforskrivning: (
            state: State,
            {
                receptforskrivning,
            }: {
                receptforskrivning: StoredReceptforskrivning;
            }
        ) => {
            const receptforskrivningar = new Map(state.receptforskrivningar);

            receptforskrivningar.set(receptforskrivning.id, receptforskrivning);

            state.receptforskrivningar = receptforskrivningar;
        },
        removeReceptforskrivning: (
            state: State,
            {
                id,
            }: {
                id?: string;
            }
        ) => {
            if (!id) {
                return;
            }

            const receptforskrivningar = new Map(state.receptforskrivningar);

            receptforskrivningar.delete(id);

            state.receptforskrivningar = receptforskrivningar;
        },
        updateStatus: (state: State, { id, status }: { id: string; status: SendStatus }) => {
            const updatedStatus = new Map<string, SendStatus>(state.status);

            updatedStatus.set(id, status);

            state.status = updatedStatus;
        },
        partialUpdateReceptforskrivning: (
            state: State,
            {
                receptforskrivning,
                id,
            }: {
                receptforskrivning: Partial<StoredReceptforskrivning>;
                id: StoredReceptforskrivning["id"];
            }
        ) => {
            const receptforskrivningar = new Map(state.receptforskrivningar);

            const foundReceptforskrivning = receptforskrivningar.get(id);

            if (!foundReceptforskrivning) {
                return;
            }

            receptforskrivningar.set(id, {
                ...foundReceptforskrivning,
                ...receptforskrivning,
            });

            state.receptforskrivningar = receptforskrivningar;
        },
    },
    actions: {
        ...baseActions({ moduleName: "receptforskrivning" }),
    },
};
